/**
 * Bannerul de acord pentru cookie-uri (vanilla-cookieconsent v3), ePrivacy / Legea 506/2004 art. 4.
 *
 * Doua categorii:
 *  - `necessary` (readOnly): autentificarea, preferintele de interfata, alegerea din banner si
 *    filmele YouTube (pornite doar la click, obligatorii la pasul de inscriere — decizia owner-ului
 *    din 22.09);
 *  - `analytics`: Azure Application Insights. Cookie-urile `ai_*` pornesc OPRITE
 *    (`telemetry/app-insights.ts`) si se aprind doar cu acord.
 *
 * Fiecare alegere (prima si orice schimbare) pleaca spre backend ca dovada
 * (`POST /api/cookie-consents`), fire-and-forget: un esec nu blocheaza pagina, iar alegerea ramane
 * aplicata local, in `cc_cookie`.
 *
 * ⚠️ `REVIZIE_ACORD` se creste doar cand se schimba ce cookie-uri punem: la crestere, bannerul
 * reapare la toti si cere acordul din nou.
 */

import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "@/styles/cookie-consent.css";

import { inregistreazaAcordCookie } from "@/api/generated/sdk.gen";
import { DOCUMENT_LINKS } from "@/constants/config";
import { handleResponse } from "@/core/api/response-handler";
import { aplicaAcordAnaliza } from "@/telemetry/app-insights";

import { construiestePayloadAcord } from "./consent-payload";

export const REVIZIE_ACORD = 1;

let pornit = false;

function aplicaStarea(): void {
    aplicaAcordAnaliza(CookieConsent.acceptedCategory("analytics"));
}

async function trimiteDovada(): Promise<void> {
    try {
        const preferinte = CookieConsent.getUserPreferences();
        const body = construiestePayloadAcord({
            consentId: CookieConsent.getCookie("consentId"),
            acceptType: preferinte.acceptType,
            acceptedCategories: preferinte.acceptedCategories,
            revizie: REVIZIE_ACORD,
        });
        handleResponse(await inregistreazaAcordCookie({ body }));
    } catch (e) {
        // Dovada lipsa nu e un motiv sa stricam pagina; alegerea e deja aplicata local.
        console.warn("Acord cookie-uri: dovada nu a putut fi trimisa", e);
    }
}

const linkPolitici =
    `<a href="${DOCUMENT_LINKS.POLITICA_CONFIDENTIALITATE}" target="_blank" rel="noopener">Politica de confidențialitate</a>` +
    `<a href="${DOCUMENT_LINKS.POLITICA_COOKIES}" target="_blank" rel="noopener">Informații despre cookie-uri</a>`;

/** In fereastra de preferinte nu exista subsolul flex al bannerului: linkurile au nevoie de separator. */
const linkPoliticiInText = linkPolitici.replace("</a><a", "</a> · <a");

const coloane = { nume: "Nume", furnizor: "Furnizor", scop: "Scop", durata: "Durată" };

/** O singura data pe incarcare de pagina; apelurile ulterioare nu fac nimic. */
export function pornesteAcordCookie(): void {
    if (pornit) return;
    pornit = true;

    void CookieConsent.run({
        revision: REVIZIE_ACORD,
        guiOptions: {
            consentModal: { layout: "box", position: "bottom center", equalWeightButtons: true },
            preferencesModal: { layout: "box", equalWeightButtons: true },
        },
        categories: {
            necessary: { enabled: true, readOnly: true },
            analytics: {
                autoClear: {
                    cookies: [{ name: /^ai_/ }],
                },
            },
        },
        onFirstConsent: () => {
            void trimiteDovada();
        },
        onConsent: aplicaStarea,
        onChange: () => {
            aplicaStarea();
            void trimiteDovada();
        },
        language: {
            default: "ro",
            translations: {
                ro: {
                    consentModal: {
                        title: "Folosim cookie-uri",
                        description:
                            "Unele sunt necesare ca platforma să funcționeze: te țin autentificat și îți păstrează alegerile. " +
                            "Cu acordul tău, folosim și cookie-uri de analiză, ca să vedem ce pagini se încarcă greu sau dau erori. " +
                            "Le poți schimba oricând din „Setări cookie-uri”.",
                        acceptAllBtn: "Accept tot",
                        acceptNecessaryBtn: "Doar necesare",
                        showPreferencesBtn: "Aleg eu",
                        footer: linkPolitici,
                    },
                    preferencesModal: {
                        title: "Setări cookie-uri",
                        acceptAllBtn: "Accept tot",
                        acceptNecessaryBtn: "Doar necesare",
                        savePreferencesBtn: "Salvează alegerea",
                        closeIconLabel: "Închide",
                        sections: [
                            {
                                description:
                                    "Alege ce cookie-uri accepți. Cele necesare nu se pot opri: fără ele nu te poți autentifica și nu poți parcurge înscrierea.",
                            },
                            {
                                title: "Necesare",
                                description:
                                    "Autentificarea, preferințele de afișare, alegerea ta de aici și filmele de pe YouTube de la înscriere și din lecții. " +
                                    "Filmele se încarcă din youtube-nocookie.com și pornesc doar când apeși pe ele.",
                                linkedCategory: "necessary",
                                cookieTable: {
                                    headers: coloane,
                                    body: [
                                        { nume: "token de autentificare (stocare locală)", furnizor: "Școala Ecoterrienilor", scop: "Te ține autentificat", durata: "Până la deconectare" },
                                        { nume: "ecotic.last-email (stocare locală)", furnizor: "Școala Ecoterrienilor", scop: "Precompletează emailul la autentificare", durata: "Persistent" },
                                        { nume: "sidebar_state", furnizor: "Școala Ecoterrienilor", scop: "Ține minte dacă meniul e deschis", durata: "7 zile" },
                                        { nume: "cc_cookie", furnizor: "Școala Ecoterrienilor", scop: "Ține minte alegerea ta de aici", durata: "6 luni" },
                                        { nume: "YouTube (youtube-nocookie.com)", furnizor: "Google", scop: "Redă filmele, doar după ce apeși pe ele", durata: "Stabilită de Google" },
                                    ],
                                },
                            },
                            {
                                title: "Analiză",
                                description:
                                    "Azure Application Insights (Microsoft): numărăm vizitele și găsim paginile care se încarcă greu sau dau erori. " +
                                    "Fără acord, erorile se raportează tot, dar fără să te putem recunoaște de la o vizită la alta.",
                                linkedCategory: "analytics",
                                cookieTable: {
                                    headers: coloane,
                                    body: [
                                        { nume: "ai_user", furnizor: "Microsoft", scop: "Identificator anonim de vizitator", durata: "1 an" },
                                        { nume: "ai_session", furnizor: "Microsoft", scop: "Grupează vizitele dintr-o sesiune", durata: "30 de minute de inactivitate" },
                                        { nume: "ai_authUser", furnizor: "Microsoft", scop: "Leagă sesiunea de contul autentificat", durata: "Sesiunea browserului" },
                                    ],
                                },
                            },
                            {
                                title: "Mai multe informații",
                                description: linkPoliticiInText,
                            },
                        ],
                    },
                },
            },
        },
    });
}

/** Redeschide fereastra de preferinte („Setări cookie-uri”). */
export function deschideSetariCookie(): void {
    CookieConsent.showPreferences();
}
